<script setup>
import { ref, computed, onMounted } from 'vue';
import segueDashboardAPI from 'dashboard/api/segueDashboard';
import PeriodFilter from './components/PeriodFilter.vue';
import BanksOverview from './components/BanksOverview.vue';
import ConversaModal from './components/ConversaModal.vue';

// v91: Visão Geral leve (views agregadas). v93 (feedback Kairo): período
// selecionável na tela toda, cards dos bancos de volta (BanksOverview),
// AO VIVO sem o "60s", e Atribuídos/Atendidas CLICÁVEIS -> janela de
// clientes (ofertas por banco + etiquetas + WhatsApp estilo iPhone).
const isLoading = ref(false);
const error = ref(null);
const data = ref(null);
const period = ref('today');
const customFrom = ref(null);
const customTo = ref(null);

const load = async (silent = false) => {
  if (!silent) isLoading.value = true;
  error.value = null;
  try {
    const { data: resp } = await segueDashboardAPI.fetchVisaoGeral({
      period: period.value,
      from: customFrom.value,
      to: customTo.value,
    });
    if (resp.error) error.value = resp.error;
    else data.value = resp;
  } catch (e) {
    error.value = e.message || 'Falha ao carregar';
  } finally {
    isLoading.value = false;
  }
};

const onPeriodChange = params => {
  period.value = params.period;
  customFrom.value = params.from || null;
  customTo.value = params.to || null;
  load();
};

// v113 (Kairo 07/07): removido o auto-refresh de 1 min. Cada aba aberta na
// Visao Geral refazia GET /visao_geral por minuto, e o service varre VARIAS
// views NAO materializadas (dashboard_propostas_dia/ofertas_dia/consultas_dia/
// ofertas_melhor) -> scan ao vivo na UY3_PROPOSTAS_ASSINATURA e afins. Agora
// carrega so ao abrir; atualizar = botao refresh (@click=load) ou trocar
// periodo. BanksOverview (5 min) segue como estava.
onMounted(() => {
  load();
});

const brl = v =>
  (v || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
const brlFull = v =>
  (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtHoje = computed(() => {
  const p = data.value?.period;
  if (!p) return '';
  const f = d => d.split('-').reverse().join('/');
  return p.from === p.to ? f(p.from) : `${f(p.from)} — ${f(p.to)}`;
});

const k = computed(() => data.value?.kpis || {});
const of = computed(() => data.value?.ofertas_dia || null);
const cm = computed(() => data.value?.contratos_mes || []);
const agentes = computed(() => data.value?.agentes || []);

const CAT_COLORS = {
  Pagos: 'bg-n-teal-9',
  'Em Análise': 'bg-n-blue-9',
  'Aguardando Pagamento': 'bg-n-amber-9',
  'Aguardando Assinatura': 'bg-n-amber-9',
  Cancelados: 'bg-n-ruby-9',
  Reprovados: 'bg-n-ruby-9',
  Outros: 'bg-n-slate-9',
};

const pctFacta = computed(() => {
  if (!of.value) return 0;
  const t = of.value.total?.valor || 0;
  return t > 0 ? Math.round(((of.value.facta?.valor || 0) / t) * 100) : 0;
});

const maxConversas = computed(() =>
  Math.max(1, ...agentes.value.map(a => a.atribuidos))
);

const spark = computed(() => {
  const serie = data.value?.evolucao_30d || [];
  if (!serie.length) return null;
  const vals = serie.map(p => p.valor);
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const W = 300;
  const H = 90;
  const pts = serie.map((p, i) => {
    const x = 8 + (i * (W - 16)) / Math.max(serie.length - 1, 1);
    const y = 8 + (H - 16) * (1 - (p.valor - min) / Math.max(max - min, 1));
    return [x.toFixed(1), y.toFixed(1)];
  });
  const line = pts.map(p => p.join(',')).join(' ');
  const area = `${pts[0][0]},${H - 4} ${line} ${pts[pts.length - 1][0]},${H - 4}`;
  const last = serie[serie.length - 1];
  return { line, area, lastX: pts[pts.length - 1][0], lastY: pts[pts.length - 1][1], last };
});

const rankCls = i => {
  if (i === 0) return 'text-n-teal-11 font-bold';
  if (i === 1) return 'text-n-teal-11/80 font-semibold';
  if (i === 2) return 'text-n-teal-11/60 font-semibold';
  return 'text-n-slate-11';
};

const statusDot = s => {
  if (s === 'online') return 'bg-n-teal-9';
  if (s === 'busy') return 'bg-n-amber-9';
  return 'bg-n-slate-8';
};

// ------- v93: drill-down de leads do agente + conversa estilo iPhone -------
const leadsModal = ref(null); // { agente, tipo, loading, leads[] }
const conversaPhone = ref(null);
const conversaNome = ref('');

const abrirLeads = async (agente, tipo) => {
  leadsModal.value = { agente, tipo, loading: true, leads: [], total: 0 };
  try {
    const { data: resp } = await segueDashboardAPI.fetchAgenteLeads({
      agentId: agente.id,
      tipo,
      period: period.value,
      from: customFrom.value,
      to: customTo.value,
    });
    leadsModal.value = {
      agente,
      tipo,
      loading: false,
      leads: resp.leads || [],
      total: resp.total || 0,
      error: resp.error,
    };
  } catch (e) {
    leadsModal.value = { agente, tipo, loading: false, leads: [], error: e.message };
  }
};

const abrirWhats = lead => {
  conversaPhone.value = lead.telefone || '';
  conversaNome.value = lead.nome || '';
};

const fecharTudo = () => {
  conversaPhone.value = null;
  leadsModal.value = null;
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="overflow-auto p-4 w-full h-full bg-n-background">
    <div class="flex flex-col gap-4 w-full">
      <!-- Header -->
      <div class="flex flex-wrap gap-4 justify-between items-center">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">Visão Geral</h1>
          <p class="mt-0.5 text-sm text-n-slate-11">{{ fmtHoje }}</p>
        </div>
        <div class="flex flex-wrap gap-3 items-center">
          <PeriodFilter v-model="period" @change="onPeriodChange" />
          <span
            class="inline-flex gap-2 items-center px-3 py-1.5 text-xs font-semibold rounded-full border border-n-teal-7 text-n-teal-11 bg-n-teal-3"
          >
            <span class="rounded-full animate-pulse size-1.5 bg-n-teal-9" />
            AO VIVO
          </span>
          <button
            type="button"
            class="flex gap-1.5 items-center px-4 h-9 text-sm font-semibold text-white rounded-lg shadow-sm bg-n-brand hover:opacity-90"
            @click="load()"
          >
            <span class="i-lucide-refresh-cw size-4" />
            Atualizar
          </button>
        </div>
      </div>

      <p v-if="error" class="text-sm text-n-ruby-11">{{ error }}</p>
      <p v-if="isLoading && !data" class="text-sm text-n-slate-11">
        Carregando…
      </p>

      <template v-if="data">
        <!-- KPIs -->
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <div
            class="p-3.5 rounded-xl border"
            :class="
              k.fila_sem_agente > 0
                ? 'border-n-ruby-7 bg-n-ruby-2'
                : 'border-n-weak bg-n-solid-1'
            "
          >
            <span class="text-[11px] font-medium uppercase tracking-wide text-n-slate-11">
              Fila sem agente
            </span>
            <p
              class="mt-1 text-2xl font-bold tabular-nums"
              :class="k.fila_sem_agente > 0 ? 'text-n-ruby-11' : 'text-n-slate-12'"
            >
              {{ k.fila_sem_agente }}
            </p>
          </div>
          <div class="p-3.5 rounded-xl border border-n-weak bg-n-solid-1">
            <span class="text-[11px] font-medium uppercase tracking-wide text-n-slate-11">
              CLT IA
            </span>
            <p class="mt-1 text-2xl font-bold tabular-nums text-n-slate-12">
              {{ k.clt_ia }}
            </p>
          </div>
          <div class="p-3.5 rounded-xl border border-n-weak bg-n-solid-1">
            <span class="text-[11px] font-medium uppercase tracking-wide text-n-slate-11">
              Atribuídos agentes
            </span>
            <p class="mt-1 text-2xl font-bold tabular-nums text-n-slate-12">
              {{ k.atribuidos_agentes }}
            </p>
          </div>
          <div class="p-3.5 rounded-xl border border-n-weak bg-n-solid-1">
            <span class="text-[11px] font-medium uppercase tracking-wide text-n-slate-11">
              Conversas
            </span>
            <p class="mt-1 text-2xl font-bold tabular-nums text-n-slate-12">
              {{ k.conversas_dia }}
            </p>
          </div>
          <div class="p-3.5 rounded-xl border border-n-weak bg-n-solid-1">
            <span class="text-[11px] font-medium uppercase tracking-wide text-n-slate-11">
              Consultas CLT
            </span>
            <p class="mt-1 text-2xl font-bold tabular-nums text-n-slate-12">
              {{ k.consultas_clt }}
            </p>
          </div>
          <div class="p-3.5 rounded-xl border border-n-weak bg-n-solid-1">
            <span class="text-[11px] font-medium uppercase tracking-wide text-n-slate-11">
              Agentes ativos
            </span>
            <p class="mt-1 text-2xl font-bold tabular-nums text-n-slate-12">
              {{ k.agentes_online
              }}<span class="text-sm font-medium text-n-slate-11">/{{ k.agentes_total }}</span>
            </p>
          </div>
        </div>

        <!-- Painéis -->
        <div class="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <div class="p-4 rounded-xl border border-n-weak bg-n-solid-1 lg:col-span-1">
            <div class="flex justify-between items-baseline mb-2">
              <h3 class="text-sm font-semibold text-n-slate-12">Ofertas</h3>
              <router-link
                :to="{ name: 'segue_dashboard_ofertas' }"
                class="text-xs font-semibold text-n-brand hover:underline"
              >
                abrir →
              </router-link>
            </div>
            <p class="text-3xl font-bold tabular-nums text-n-slate-12">
              {{ brl(of?.total?.valor) }}
            </p>
            <p class="text-sm text-n-slate-11">
              {{ of?.total?.clientes || 0 }} cliente(s)
            </p>
            <div
              v-if="(of?.total?.valor || 0) > 0"
              class="flex overflow-hidden gap-0.5 mt-3 h-3 rounded-md"
            >
              <span class="bg-n-amber-9 rounded-l-md" :style="{ width: pctFacta + '%' }" />
              <span class="flex-1 rounded-r-md bg-n-blue-9" />
            </div>
            <div class="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-n-slate-11">
              <span>
                <span class="inline-block mr-1 rounded-sm size-2 bg-n-amber-9" />
                FACTA
                <b class="tabular-nums text-n-slate-12">{{ brl(of?.facta?.valor) }}</b>
              </span>
              <span>
                <span class="inline-block mr-1 rounded-sm size-2 bg-n-blue-9" />
                UY3
                <b class="tabular-nums text-n-slate-12">{{ brl(of?.uy3?.valor) }}</b>
              </span>
              <span v-if="of?.c6?.clientes">
                C6
                <b class="tabular-nums text-n-slate-12">{{ brl(of?.c6?.valor) }}</b>
              </span>
            </div>
          </div>

          <div class="p-4 rounded-xl border border-n-weak bg-n-solid-1">
            <div class="flex justify-between items-baseline mb-2">
              <h3 class="text-sm font-semibold text-n-slate-12">Contratos</h3>
              <router-link
                :to="{ name: 'segue_dashboard_contratos' }"
                class="text-xs font-semibold text-n-brand hover:underline"
              >
                abrir →
              </router-link>
            </div>
            <div class="flex flex-col gap-2 mt-1">
              <div
                v-for="c in cm"
                :key="c.categoria"
                class="flex gap-2 items-center text-sm"
              >
                <span
                  class="rounded-full size-2"
                  :class="CAT_COLORS[c.categoria] || 'bg-n-slate-9'"
                />
                <span class="text-n-slate-11">
                  {{ c.categoria }}
                  <span class="text-xs">({{ c.qtd }})</span>
                </span>
                <b class="ml-auto tabular-nums text-n-slate-12">
                  {{ brl(c.valor) }}
                </b>
              </div>
              <p v-if="!cm.length" class="text-sm text-n-slate-11">
                Sem contratos no período
              </p>
            </div>
          </div>

          <div class="p-4 rounded-xl border border-n-weak bg-n-solid-1">
            <div class="flex justify-between items-baseline mb-2">
              <h3 class="text-sm font-semibold text-n-slate-12">
                Evolução · 30 dias
              </h3>
              <span class="text-[10px] uppercase tracking-wide text-n-slate-11">
                ofertas/dia
              </span>
            </div>
            <div v-if="spark" class="relative">
              <svg viewBox="0 0 300 94" class="w-full">
                <polygon :points="spark.area" class="fill-n-teal-9/15" />
                <polyline
                  :points="spark.line"
                  fill="none"
                  class="stroke-n-teal-9"
                  stroke-width="2"
                  stroke-linejoin="round"
                  stroke-linecap="round"
                />
                <circle :cx="spark.lastX" :cy="spark.lastY" r="3.5" class="fill-n-teal-9" />
              </svg>
              <p class="mt-1 text-xs text-n-slate-11">
                Hoje:
                <b class="tabular-nums text-n-slate-12">{{ brl(spark.last.valor) }}</b>
                · {{ spark.last.clientes }} cliente(s)
              </p>
            </div>
          </div>
        </div>

        <!-- v93: cards dos bancos com status (de volta, a pedido do Kairo) -->
        <BanksOverview
          :period="period"
          :custom-from="customFrom"
          :custom-to="customTo"
        />

        <!-- Ranking de agentes -->
        <div class="overflow-x-auto rounded-xl border border-n-weak">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left bg-n-solid-2 text-n-slate-11">
                <th class="px-3 py-2.5 font-medium">#</th>
                <th class="px-3 py-2.5 font-medium">Agente</th>
                <th class="px-3 py-2.5 font-medium text-right">Atribuídos</th>
                <th class="px-3 py-2.5 font-medium">Volume</th>
                <th class="px-3 py-2.5 font-medium text-right">Atendidas</th>
                <th class="px-3 py-2.5 font-medium text-right">Abertas</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(a, i) in agentes"
                :key="a.id"
                class="border-t border-n-weak hover:bg-n-alpha-1"
              >
                <td class="px-3 py-2.5 tabular-nums" :class="rankCls(i)">
                  {{ i + 1 }}º
                </td>
                <td class="px-3 py-2.5">
                  <span class="flex gap-2 items-center">
                    <span class="rounded-full size-2" :class="statusDot(a.availability)" />
                    <span class="font-medium text-n-slate-12">{{ a.nome }}</span>
                  </span>
                </td>
                <td class="px-3 py-2.5 text-right">
                  <button
                    type="button"
                    class="font-semibold tabular-nums underline text-n-teal-11 hover:opacity-75 disabled:no-underline disabled:text-n-slate-12"
                    :disabled="!a.atribuidos"
                    @click="abrirLeads(a, 'atribuidos')"
                  >
                    {{ a.atribuidos }}
                  </button>
                </td>
                <td class="px-3 py-2.5 w-40">
                  <span class="block overflow-hidden h-1.5 rounded-full bg-n-alpha-2">
                    <span
                      class="block h-full rounded-full bg-n-teal-9"
                      :style="{ width: (a.atribuidos / maxConversas) * 100 + '%' }"
                    />
                  </span>
                </td>
                <td class="px-3 py-2.5 text-right">
                  <button
                    type="button"
                    class="tabular-nums underline text-n-teal-11 hover:opacity-75 disabled:no-underline disabled:text-n-slate-12"
                    :disabled="!a.atendidas"
                    @click="abrirLeads(a, 'atendidas')"
                  >
                    {{ a.atendidas }}
                  </button>
                </td>
                <td class="px-3 py-2.5 text-right tabular-nums text-n-slate-11">
                  {{ a.abertas }}
                </td>
              </tr>
              <tr v-if="!agentes.length">
                <td colspan="6" class="px-3 py-6 text-center text-n-slate-11">
                  Sem atividade de agentes no período
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <!-- v93: janela de leads do agente -->
    <div
      v-if="leadsModal"
      class="flex fixed inset-0 z-40 justify-center items-center p-4 bg-black/60"
      @click.self="leadsModal = null"
    >
      <div class="flex flex-col w-full max-w-5xl max-h-[85vh] rounded-xl border shadow-xl border-n-weak bg-n-background">
        <div class="flex justify-between items-center px-4 py-3 border-b border-n-weak">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ leadsModal.agente.nome }} ·
            {{ leadsModal.tipo === 'atendidas' ? 'Atendidas' : 'Atribuídos' }}
            <span class="font-normal text-n-slate-11">
              · {{ leadsModal.total }} cliente(s)
            </span>
          </h3>
          <button
            type="button"
            class="px-3 py-1 text-xs font-bold text-white bg-red-500 rounded-lg hover:bg-red-600"
            @click="leadsModal = null"
          >
            ✕ Fechar
          </button>
        </div>
        <div class="overflow-auto p-4">
          <p v-if="leadsModal.loading" class="text-sm text-n-slate-11">Carregando…</p>
          <p v-else-if="leadsModal.error" class="text-sm text-n-ruby-11">
            {{ leadsModal.error }}
          </p>
          <table v-else class="w-full text-sm">
            <thead>
              <tr class="text-left bg-n-solid-2 text-n-slate-11">
                <th class="px-3 py-2 font-medium">Cliente</th>
                <th class="px-3 py-2 font-medium">CPF</th>
                <th class="px-3 py-2 font-medium">Telefone</th>
                <th class="px-3 py-2 font-medium text-right">Oferta UY3</th>
                <th class="px-3 py-2 font-medium text-right">Oferta FACTA</th>
                <th class="px-3 py-2 font-medium text-right">Oferta C6</th>
                <th class="px-3 py-2 font-medium">Status</th>
                <th class="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="l in leadsModal.leads"
                :key="l.conversation_id"
                class="border-t border-n-weak hover:bg-n-alpha-1"
              >
                <td class="px-3 py-2 font-medium text-n-slate-12">{{ l.nome }}</td>
                <td class="px-3 py-2 tabular-nums text-n-slate-12">{{ l.cpf || '—' }}</td>
                <td class="px-3 py-2 text-n-slate-12">{{ l.telefone }}</td>
                <td class="px-3 py-2 text-right tabular-nums text-n-slate-12">
                  {{ l.ofertas?.UY3 ? brlFull(l.ofertas.UY3.valor) : '—' }}
                </td>
                <td class="px-3 py-2 text-right tabular-nums text-n-slate-12">
                  {{ l.ofertas?.FACTA ? brlFull(l.ofertas.FACTA.valor) : '—' }}
                </td>
                <td class="px-3 py-2 text-right tabular-nums text-n-slate-12">
                  {{ l.ofertas?.C6 ? brlFull(l.ofertas.C6.valor) : '—' }}
                </td>
                <td class="px-3 py-2">
                  <span
                    v-for="e in l.etiquetas"
                    :key="e"
                    class="inline-block px-1.5 py-0.5 mr-1 mb-0.5 text-[10px] font-semibold rounded bg-n-alpha-3 text-n-slate-12"
                  >
                    {{ e }}
                  </span>
                  <span v-if="!l.etiquetas?.length" class="text-n-slate-11">—</span>
                </td>
                <td class="px-3 py-2 text-right">
                  <button
                    type="button"
                    class="inline-flex justify-center items-center text-white rounded-lg size-7 bg-n-teal-9 hover:opacity-90"
                    title="Ver conversa"
                    @click="abrirWhats(l)"
                  >
                    <span class="i-lucide-message-circle size-4" />
                  </button>
                </td>
              </tr>
              <tr v-if="!leadsModal.leads.length">
                <td colspan="8" class="px-3 py-6 text-center text-n-slate-11">
                  Nenhum cliente no período
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- v93: conversa estilo iPhone -->
    <ConversaModal
      v-if="conversaPhone"
      :phone="conversaPhone"
      :nome="conversaNome"
      @close="conversaPhone = null"
      @navigate="fecharTudo"
    />
  </div>
</template>
